import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';

import {
  BottomSheetMotionController,
} from '../../utils/bottom-sheet-motion';

export type BottomSheetState = 'collapsed' | 'expanded';

const FLICK_VELOCITY_PX_MS = 0.5;
const SNAP_PROGRESS_THRESHOLD = 0.5;
const SETTLE_ANIMATION_MS = 200;

@Component({
  selector: 'app-bottom-sheet',
  standalone: true,
  templateUrl: './bottom-sheet.component.html',
  styleUrl: './bottom-sheet.component.scss',
  host: {
    class:
      'app-bottom-sheet block fixed inset-0 z-[60] overflow-hidden bg-transparent border-0 rounded-none min-h-0 max-h-none pointer-events-none motion-reduce:transition-none',
    '[class.app-bottom-sheet--collapsed]': "state === 'collapsed'",
    '[class.app-bottom-sheet--expanded]': "state === 'expanded'",
    '[class.app-bottom-sheet--dragging]': 'isDragging',
    '[attr.data-sheet-state]': 'state',
    '[style.--bottom-sheet-collapsed-height.px]': 'collapsedHeightPx',
  },
})
export class BottomSheetComponent implements AfterViewInit, OnDestroy {
  protected readonly surfaceClasses =
    'app-bottom-sheet__surface absolute inset-x-0 bottom-0 z-1 flex min-h-[400px] h-auto max-h-[90dvh] flex-col overflow-hidden pointer-events-auto rounded-t-2xl bg-surface-container-low pb-[env(safe-area-inset-bottom,0px)] motion-reduce:transition-none';

  @Input() collapsedHeightPx = 72;
  @Input() closeOnBackdrop = true;

  @Input({ required: true })
  handleAriaLabel!: string;

  @Input({ required: true })
  backdropAriaLabel!: string;

  @Output()
  readonly stateChange = new EventEmitter<BottomSheetState>();

  @ViewChild('backdrop') private readonly backdropRef?: ElementRef<HTMLElement>;
  @ViewChild('surface') private readonly surfaceRef?: ElementRef<HTMLElement>;
  @ViewChild('handle') private readonly handleRef?: ElementRef<HTMLElement>;

  state: BottomSheetState = 'collapsed';
  isDragging = false;
  isSettling = false;

  private motion?: BottomSheetMotionController;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    this.bindMotion();
  }

  ngOnDestroy(): void {
    this.motion?.destroy();
  }

  expand(): void {
    this.motion?.settleTo(1);
  }

  collapse(): void {
    this.motion?.settleTo(0);
  }

  toggle(): void {
    this.motion?.settleTo(this.state === 'expanded' ? 0 : 1);
  }

  onHandleActivate(event?: Event): void {
    event?.preventDefault();
    if (this.isSettling) {
      return;
    }

    this.toggle();
  }

  onBackdropClick(): void {
    if (
      !this.closeOnBackdrop
      || this.isDragging
      || this.isSettling
      || this.state === 'collapsed'
    ) {
      return;
    }

    this.backdropRef?.nativeElement.blur();
    this.collapse();
  }

  private bindMotion(): void {
    const handle = this.handleRef?.nativeElement;
    const surface = this.surfaceRef?.nativeElement;

    if (!handle || !surface) {
      return;
    }

    this.motion?.destroy();
    this.motion = new BottomSheetMotionController(
      {
        handle,
        surface,
        backdrop: this.backdropRef?.nativeElement ?? null,
        draggingClassHost: this.elementRef.nativeElement,
      },
      {
        initialEndpoint: 0,
        getTravelPx: () => this.getTravelPx(surface),
        resolveEndpoint: ({ progress, velocityY }) => {
          if (velocityY < -FLICK_VELOCITY_PX_MS) {
            return 1;
          }
          if (velocityY > FLICK_VELOCITY_PX_MS) {
            return 0;
          }
          return progress >= SNAP_PROGRESS_THRESHOLD ? 1 : 0;
        },
        tapEndpoint: (current) => (current === 1 ? 0 : 1),
        cleanupOnSettled: () => 'clear-all',
        settleDurationMs: SETTLE_ANIMATION_MS,
        draggingClass: 'app-bottom-sheet--dragging',
        onDraggingChange: (dragging) => {
          this.isDragging = dragging;
          this.changeDetectorRef.markForCheck();
        },
        onSettleStart: () => {
          this.isSettling = true;
          this.changeDetectorRef.markForCheck();
        },
        onSettled: (endpoint) => {
          this.isSettling = false;
          this.state = endpoint === 1 ? 'expanded' : 'collapsed';
          this.stateChange.emit(this.state);
          this.changeDetectorRef.markForCheck();
        },
        onSettleCancel: () => {
          this.isSettling = false;
          this.changeDetectorRef.markForCheck();
        },
      },
    );
  }

  private getTravelPx(surface: HTMLElement): number {
    const safeAreaInsetBottom =
      parseFloat(getComputedStyle(surface).paddingBottom) || 0;
    const peekHeight = this.collapsedHeightPx + safeAreaInsetBottom;
    return Math.max(surface.offsetHeight - peekHeight, 1);
  }
}
